// Multi-step dialog for importing course content from a local file.
//
// Steps:
//   1. Upload   — drag-and-drop or click to upload a .docx or .txt file
//   2. Parsing  — loading while text is extracted and analyzed by AI
//   3. Mapping  — AI-suggested sections; user confirms or changes field assignments
//   4. Error    — surface actionable error messages

import { FileUp, Loader2, X } from 'lucide-react';
import { useCallback, useContext, useState } from 'react';

import { showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import type { PulledFocusImport } from '@/Pages/Courses/CourseEditContext';
import CourseEditContext from '@/Pages/Courses/CourseEditContext';

const ACCEPTED_TYPES = '.docx,.txt';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

type FieldKey = 'description' | 'prereq' | 'extras' | 'outcomes' | 'focuses' | 'ignore';
type SkillType = 'topic' | 'skill' | 'question';

const FIELD_LABELS: Record<FieldKey, string> = {
	description: 'Description',
	prereq: 'Prerequisites',
	extras: 'Additional Info',
	outcomes: 'Outcomes',
	focuses: 'Unit of Focus',
	ignore: 'Ignore',
};

const SKILL_TYPE_LABELS: Record<SkillType, string> = {
	topic: 'Subtopic/Description',
	skill: 'Skill',
	question: 'Essential Question',
};

interface DocumentSection {
	heading: string;
	content: string;
	suggestedField: FieldKey;
}

interface SectionMapping extends DocumentSection {
	selectedField: FieldKey;
	skillType: SkillType;
}

type Step = 'upload' | 'parsing' | 'mapping' | 'error';

interface FileImportPanelProps {
	open: boolean;
	onClose: () => void;
}

/** Converts plain text content (with "• " bullet prefix) into minimal HTML for TipTap. */
export function contentToHtml(text: string): string {
	const lines = text.split('\n').filter((l) => l.trim());
	const bulletLines = lines.filter((l) => l.startsWith('• '));
	const isList = bulletLines.length > 0 && bulletLines.length === lines.length;

	if (isList) {
		const items = lines.map((l) => `<li>${l.replace(/^•\s*/, '').trim()}</li>`).join('');
		return `<ul>${items}</ul>`;
	}

	return lines.map((l) => `<p>${l.trim()}</p>`).join('');
}

/** Splits a section's content into individual outcome strings. */
export function extractOutcomeLines(content: string): string[] {
	return content
		.split('\n')
		.map((l) => l.replace(/^(?:[•-]|\d+\.)\s*/, '').trim())
		.filter(Boolean);
}

/** Extracts plain text from a .docx or .txt file client-side. */
async function extractText(file: File): Promise<string> {
	if (file.name.endsWith('.docx')) {
		const { default: mammoth } = await import('mammoth');
		const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
		return result.value;
	}
	return file.text();
}

export function FileImportPanel({ open, onClose }: FileImportPanelProps) {
	const {
		updateDescription,
		updatePrereq,
		updateAdditionalInfo,
		addOutcomeWithText,
		importFocusFromPull,
	} = useContext(CourseEditContext);

	const [step, setStep] = useState<Step>('upload');
	const [mappings, setMappings] = useState<SectionMapping[]>([]);
	const [errorMessage, setErrorMessage] = useState('');
	const [isDragging, setIsDragging] = useState(false);

	const handleClose = () => {
		setMappings([]);
		setErrorMessage('');
		setStep('upload');
		onClose();
	};

	const processFile = useCallback(async (file: File) => {
		if (file.size > MAX_BYTES) {
			setErrorMessage('File is too large. Maximum size is 5 MB.');
			setStep('error');
			return;
		}
		if (!file.name.endsWith('.docx') && !file.name.endsWith('.txt')) {
			setErrorMessage('Unsupported file type. Please upload a .docx or .txt file.');
			setStep('error');
			return;
		}

		setStep('parsing');
		try {
			const text = await extractText(file);

			if (!text.trim()) {
				throw new Error('The file appears to be empty.');
			}

			const { data: result, error: fnError } = await supabase.functions.invoke('import-from-text', {
				body: { text },
			});
			if (fnError) throw new Error(fnError.message ?? 'Import failed');

			const sections: DocumentSection[] = result.sections;
			setMappings(
				sections.map((s) => ({ ...s, selectedField: s.suggestedField, skillType: 'topic' }))
			);
			setStep('mapping');
		} catch (err) {
			const msg = err instanceof Error ? err.message : 'Failed to analyze document';
			setErrorMessage(msg);
			setStep('error');
		}
	}, []);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) processFile(file);
		// Reset so the same file can be re-selected after an error
		e.target.value = '';
	};

	const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file) processFile(file);
	};

	const handleFieldChange = (index: number, field: FieldKey) => {
		setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, selectedField: field } : m)));
	};

	const handleSkillTypeChange = (index: number, skillType: SkillType) => {
		setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, skillType } : m)));
	};

	const handleImport = () => {
		const toImport = mappings.filter((m) => m.selectedField !== 'ignore');

		for (const section of toImport) {
			const html = contentToHtml(section.content);
			switch (section.selectedField) {
				case 'description':
					updateDescription(html);
					break;
				case 'prereq':
					// Prereq is a plain-text input, not a rich-text editor
					updatePrereq(section.content.trim());
					break;
				case 'extras':
					updateAdditionalInfo(html);
					break;
				case 'outcomes': {
					const lines = extractOutcomeLines(section.content);
					for (const line of lines) {
						addOutcomeWithText(line);
					}
					break;
				}
				case 'focuses': {
					const bullets = extractOutcomeLines(section.content);
					const payload: PulledFocusImport = {
						title: section.heading,
						skills: bullets.map((text) => ({ skill_type: section.skillType, skill_text: text })),
					};
					importFocusFromPull(payload);
					break;
				}
			}
		}

		showSuccessToast('Content imported — review and save your draft.');
		handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
			<DialogContent className="max-w-[800px] max-h-[85vh] overflow-hidden flex flex-col p-0">
				<DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
					<div className="flex items-center gap-2">
						<FileUp size={18} className="text-primary" />
						<DialogTitle>Import from File</DialogTitle>
					</div>
					{step === 'mapping' && (
						<p className="text-[13px] text-muted-foreground mt-1">
							Review each section and assign it to the correct course field, then click{' '}
							<strong>Import Selected</strong>.
						</p>
					)}
				</DialogHeader>

				<div className="flex-1 overflow-y-auto px-6 py-5">
					{/* Step: upload */}
					{step === 'upload' && (
						<label
							htmlFor="file-import-input"
							onDragOver={(e) => {
								e.preventDefault();
								setIsDragging(true);
							}}
							onDragLeave={() => setIsDragging(false)}
							onDrop={handleDrop}
							className={`flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed py-14 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-secondary/40'}`}
						>
							<div className="flex items-center justify-center w-14 h-14 rounded-full bg-secondary">
								<FileUp size={24} className="text-primary" />
							</div>
							<div>
								<p className="font-medium text-[15px]">Drop a file here</p>
								<p className="text-[13px] text-muted-foreground mt-1">or click to browse</p>
							</div>
							<p className="text-[12px] text-muted-foreground">
								Supported: .docx, .txt &nbsp;·&nbsp; Max 5 MB
							</p>
							<input
								id="file-import-input"
								type="file"
								accept={ACCEPTED_TYPES}
								onChange={handleFileChange}
								className="hidden"
							/>
						</label>
					)}

					{/* Step: parsing */}
					{step === 'parsing' && (
						<div className="flex flex-col items-center gap-3 py-12 text-center">
							<Loader2 size={28} className="animate-spin text-primary" />
							<p className="text-[14px] font-medium">Analyzing your document…</p>
							<p className="text-[12px] text-muted-foreground">This may take a few seconds.</p>
						</div>
					)}

					{/* Step: error */}
					{step === 'error' && (
						<div className="flex flex-col items-center gap-4 py-8 text-center">
							<div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10">
								<X size={24} className="text-destructive" />
							</div>
							<div>
								<p className="font-medium text-[15px] text-destructive">Something went wrong</p>
								<p className="text-[13px] text-muted-foreground mt-1 max-w-xl">{errorMessage}</p>
							</div>
							<Button variant="outline" onClick={() => setStep('upload')}>
								Try again
							</Button>
						</div>
					)}

					{/* Step: mapping */}
					{step === 'mapping' && (
						<div className="flex flex-col gap-3">
							{mappings.map((section, idx) => (
								<div
									key={`${section.heading}-${idx}`}
									className="rounded-lg border border-border bg-secondary/30 p-4"
								>
									<div className="flex items-start justify-between gap-3 mb-3">
										<p className="text-[13px] font-semibold text-foreground leading-snug flex-1">
											{section.heading}
										</p>
										<div className="flex items-center gap-2 shrink-0">
											{section.selectedField === 'focuses' && (
												<Select
													value={section.skillType}
													onValueChange={(val) => handleSkillTypeChange(idx, val as SkillType)}
												>
													<SelectTrigger className="w-44 h-8 text-[12px]">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{(Object.keys(SKILL_TYPE_LABELS) as SkillType[]).map((key) => (
															<SelectItem key={key} value={key} className="text-[12px]">
																{SKILL_TYPE_LABELS[key]}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											)}
											<Select
												value={section.selectedField}
												onValueChange={(val) => handleFieldChange(idx, val as FieldKey)}
											>
												<SelectTrigger className="w-44 h-8 text-[12px]">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{(Object.keys(FIELD_LABELS) as FieldKey[]).map((key) => (
														<SelectItem key={key} value={key} className="text-[12px]">
															{FIELD_LABELS[key]}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>
									<p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-3 whitespace-pre-wrap">
										{section.content}
									</p>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Footer actions for mapping step */}
				{step === 'mapping' && (
					<div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border shrink-0 bg-background">
						<p className="text-[11px] text-muted-foreground">
							Importing will add to existing outcomes and units, and <strong>replace</strong>{' '}
							description, prerequisites, and additional info.
						</p>
						<div className="flex gap-2 shrink-0">
							<Button variant="outline" size="small" onClick={handleClose}>
								Cancel
							</Button>
							<Button size="small" onClick={handleImport}>
								Import Selected
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
